import { createSelector } from 'reselect'
import type { RootState } from 'shared/store/reducers'
import type {
    Allergen,
    Catalog,
    DisplayableCategory,
    DisplayableProduct,
    MapById,
    MapListById,
    MetaProduct
} from './types'
import type {
    CacAllergen,
    CacCatalog,
    CacCategory,
    CacMenu,
    CacProduct,
    CacProductGroup
} from 'shared/types'
import { catalogType } from 'shared/utils/const'
import { ws } from 'shared/store/ws'

const { CATEGORY } = catalogType

export const catalogSelector = createSelector(
    (state: RootState) => state.ecommerceCatalogState.catalog,
    (state: RootState) => state.ecommerceCatalogState.selectedAllergens,
    (state: RootState) => state.ecommerceCatalogState.withoutBacon,
    (state: RootState) => state.ecommerceCatalogState.subCategoriesId,
    (state: RootState) => state.ecommerceCatalogState.subCategoriesById,
    (state: RootState) => state.ecommerceCatalogState.categoriesById,
    (state: RootState) => state.ecommerceCatalogState.categoriesByCategoryId,
    (state: RootState) => state.ecommerceCatalogState.productsByCategoryId,
    (state: RootState) => state.ecommerceCatalogState.productGroupsByCategoryId,
    (state: RootState) => state.ecommerceCatalogState.menusByCategoryId,

    (
        catalog,
        selectedAllergens,
        withoutBacon,
        subCategoriesId,
        subCategoriesById,
        categoriesById,
        categoriesByCategoryId,
        productsByCategoryId,
        productGroupsByCategoryId,
        menusByCategoryId
    ) =>
        _catalogSelector({
            catalog,
            withoutBacon,
            selectedAllergens,
            subCategoriesId,
            subCategoriesById,
            categoriesById,
            categoriesByCategoryId,
            productsByCategoryId,
            productGroupsByCategoryId,
            menusByCategoryId
        })
)

type CatalogSelectorProps = {
    catalog: CacCatalog,
    selectedAllergens: Array<string>,
    withoutBacon: boolean,
    subCategoriesId: Array<string>,
    subCategoriesById: MapById<CacCategory>,
    categoriesById: MapById<CacCategory>,
    categoriesByCategoryId: MapListById<MetaProduct & CacCategory>,
    productsByCategoryId: MapListById<MetaProduct & CacProduct>,
    productGroupsByCategoryId: MapListById<MetaProduct & CacProductGroup>,
    menusByCategoryId: MapListById<MetaProduct & CacMenu>
}

const _catalogSelector = (props: CatalogSelectorProps): Catalog => {
    const {
        catalog = {},
        selectedAllergens = [],
        withoutBacon,
        subCategoriesId = [],
        subCategoriesById = {},
        categoriesById = [],
        categoriesByCategoryId = {},
        productsByCategoryId = {},
        productGroupsByCategoryId = {},
        menusByCategoryId = {}
    } = props

    const { categories = [] } = catalog

    let result = []

    categories.forEach((category: CacCategory) => {
        const categoryId = category.id

        // select categories in category
        let categories = []
        if (categoriesByCategoryId[categoryId]) {
            categories = [...categoriesByCategoryId[categoryId]]
        }

        // select displayable products in category
        let displayableProducts = []
        if (productsByCategoryId[categoryId]) {
            displayableProducts = [...productsByCategoryId[categoryId]]
        }
        if (productGroupsByCategoryId[categoryId]) {
            displayableProducts = [
                ...displayableProducts,
                ...productGroupsByCategoryId[categoryId]
            ]
        }
        if (menusByCategoryId[categoryId]) {
            displayableProducts = [
                ...displayableProducts,
                ...menusByCategoryId[categoryId]
            ]
        }

        // filter out inactive & unvailable products
        displayableProducts = displayableProducts.filter(
            // $FlowFixMe
            displayableProduct => displayableProduct.active
        )

        // filter out products with bacon
        if (withoutBacon) {
            displayableProducts = productsWithoutBacon(displayableProducts)
        }

        // apply selected allergens
        displayableProducts = productsWithSelectedAllergens(
            displayableProducts,
            selectedAllergens
        )

        //sort categories
        categories.sort((a, b) => a.displayOrder - b.displayOrder)

        // sort products
        displayableProducts.sort((a, b) => a.displayOrder - b.displayOrder)

        // group by sub categories
        const displayableProductsBySubCategory = displayableProducts.reduce(
            (acc, displayableProduct) => {
                const { subCategories = [], ...rest } = displayableProduct
                subCategories &&
                    subCategories.forEach(subCategoryId => {
                        if (!acc.hasOwnProperty(subCategoryId)) {
                            acc[subCategoryId] = []
                        }
                        acc[subCategoryId].push({
                            ...rest,
                            subCategoryId
                        })
                    })
                return acc
            },
            {}
        )

        const displayableProductsBySubCategoryEntries = Object.entries(
            displayableProductsBySubCategory
        )
        const hasSubCategories =
            displayableProductsBySubCategoryEntries.length > 0 &&
            category.acceptFilters
        const subCategories = []

        if (hasSubCategories) {
            // sort by subcategory order
            displayableProductsBySubCategoryEntries.sort((a, b) => {
                const idA = a[0],
                    idB = b[0]
                const indexOfSubCategoryIdA = subCategoriesId.indexOf(idA)
                const indexOfSubCategoryIdB = subCategoriesId.indexOf(idB)
                return indexOfSubCategoryIdA - indexOfSubCategoryIdB
            })
            for (const [
                subCategoryId,
                displayableProductsOfSubCategory
            ] of displayableProductsBySubCategoryEntries) {
                if (
                    displayableProductsOfSubCategory &&
                    // $FlowFixMe https://github.com/facebook/flow/issues/2174
                    displayableProductsOfSubCategory.length > 0
                ) {
                    // sort products
                    // $FlowFixMe https://github.com/facebook/flow/issues/2174
                    displayableProductsOfSubCategory.sort(
                        (a, b) => a.displayOrder - b.displayOrder
                    )
                    subCategories.push({
                        categoryId,
                        id: subCategoryId,
                        name: subCategoriesById[subCategoryId].name,
                        // $FlowFixMe https://github.com/facebook/flow/issues/2174
                        displayableProducts: displayableProductsOfSubCategory.map(
                            p => ({ ...p, subCategoryId })
                        )
                    })
                }
            }
        }

        let item = {
            id: categoryId,
            name: category.name,
            categories: categories.map(category =>
                toDisplayableCategory(category.id, categoriesById)
            )
        }

        if (hasSubCategories) {
            item = {
                ...item,
                subCategories: subCategories.map(subCategory => ({
                    ...subCategory,
                    displayableProducts: subCategory.displayableProducts.map(
                        toDisplayableProduct
                    )
                }))
            }
        } else {
            item = {
                ...item,
                displayableProducts: displayableProducts.map(
                    toDisplayableProduct
                )
            }
        }

        result.push(item)
    })

    const isWeb = ws.defaults.headers['x-platform'] === 'WEB'
    // filter out empty categories
    result = result.filter(category => {
        const hasDisplayableCategories =
            (category.categories && category.categories.length > 0) || false
        const hasDisplayableProducts =
            (category.displayableProducts &&
                category.displayableProducts.length > 0) ||
            false
        const hasDisplayableProductsInSubCategories =
            (category.subCategories &&
                category.subCategories.reduce(
                    (acc, subCategory) =>
                        acc ||
                        (subCategory.displayableProducts &&
                            subCategory.displayableProducts.length > 0),
                    false
                )) ||
            false
        return (
            (!isWeb && hasDisplayableCategories) ||
            hasDisplayableProducts ||
            hasDisplayableProductsInSubCategories
        )
    })

    return result
}

const toDisplayableProduct = (p: any): DisplayableProduct => ({
    type: p.type,
    id: p.id,
    categoryId: p?.categoryId ?? '',
    subCategoryId: p.subCategoryId,
    name: p.name,
    routeId: p.routeId,
    image: p.image,
    price: p.priceS || p.price,
    hasSelectedAllergens: p.hasSelectedAllergens,
    available: p.available,
    hasIce: p.hasIce,
    hasPickUpLater: p.pickUpLater,
    airshipTagInApp: p.airshipTagInApp,
    composable: p.composable,
    ...(p?.priceS && {
        priceS: p.priceS
    })
})

const toDisplayableCategory = (
    categoryId: string,
    categoriesById: MapById<CacCategory> | []
): DisplayableCategory => {
    //$FlowFixMe
    const displayableCategory = categoriesById[categoryId]
    return {
        id: displayableCategory.id,
        name: displayableCategory.name,
        image: displayableCategory.image,
        type: CATEGORY
    }
}

const productsWithoutBacon = (products: Array<any>): Array<any> => {
    return products.filter(product => {
        const { hasBacon = false } = product
        return !hasBacon
    })
}

export const productsWithSelectedAllergens = (
    products: Array<any>,
    selectedAllergens: Array<string>
): Array<any> => {
    return products.map(product => {
        let hasSelectedAllergens = false
        if (product.type === 'PRODUCT' || product.type === 'MENU') {
            const allergens = product.allergens || []
            hasSelectedAllergens =
                allergens.filter(allergen =>
                    selectedAllergens.includes(allergen)
                ).length > 0
        }
        return {
            ...product,
            hasSelectedAllergens
        }
    })
}

export const allergensSelector = createSelector(
    (state: RootState) =>
        (state.ecommerceCatalogState.catalog || {}).allergens || [],
    (allergens: Array<CacAllergen>) => _allergensSelector(allergens)
)

const _allergensSelector = (allergens: Array<CacAllergen>): Array<Allergen> =>
    allergens.map((allergen: CacAllergen) => ({
        code: allergen.id,
        name: allergen.name,
        iconUrl: ''
    }))
